/**
 * HealingImmersion — Spinal Light Reset
 * 
 * 3 silent breaths → Voice (~102s) → Extended radiant rest → Very slow dissolve → Completion
 * Total immersion after breathing: ~7 minutes (420s)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import BreathOrb from "../../components/BreathOrb";
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, SkipForward } from 'lucide-react';
import HealingVisualEngine from './HealingVisualEngine';
import CrystalSpineVisualEngine from './CrystalSpineVisualEngine';
import { voiceVolume, musicVolume, binauralVolume, getStoredAudioMode } from '../../utils/modeAudio';
import { captureAndSendKeepsake } from '../../utils/familyKeepsake';

const API = process.env.REACT_APP_BACKEND_URL;

// Remote audio-error telemetry — sends the exact iOS Safari error
// details to backend logs so we can debug what's actually failing
// on Michael's mum's iPhone. Fire-and-forget, no auth.
function reportAudioError(where, attempt, el, err, ctx) {
  try {
    const body = {
      where, attempt,
      error_name: err?.name || null,
      error_message: err?.message || String(err || ''),
      media_error_code: el?.error?.code ?? null,
      ready_state: el?.readyState ?? null,
      network_state: el?.networkState ?? null,
      src: el?.currentSrc || el?.src || null,
      current_time: el?.currentTime ?? null,
      duration: (el && isFinite(el.duration)) ? el.duration : null,
      paused: el?.paused ?? null,
      volume: el?.volume ?? null,
      user_agent: (typeof navigator !== 'undefined') ? navigator.userAgent : null,
      context: ctx || null,
    };
    fetch(`${API}/api/audio-debug/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* diagnostic can't fail loudly */ });
  } catch (_) { /* noop */ }
}

// ============================================
// TIMING — default 7 minute total (after breathing).
// journeyLength prop can override: "flare-up" (5m) | "daily" (20m) | "sleep" (45m).
// ============================================

const VOICE_DURATION = 102;    // Michael's recording

const JOURNEY_TIMINGS = {
  'flare-up': { total: 300,  dissolveOffset: 25 },   // 5 min
  'daily':    { total: 1200, dissolveOffset: 30 },   // 20 min
  'sleep':    { total: 2700, dissolveOffset: 60 },   // 45 min
};
const DEFAULT_TOTAL = 420;      // 7 min if no length provided (legacy)
const DEFAULT_DISSOLVE_OFFSET = 25;

// ============================================
// BREATHING — 3 breaths before audio begins
// ============================================

// Breath cycle — tuned to match the healing_breath.mp3 recording's
// natural cadence (~18.3s per cycle × 3 ≈ 55s) so on-screen prompts
// stay in lock-step with the audible breath.
const BREATH_INHALE = 5000;
const BREATH_HOLD = 3000;
const BREATH_EXHALE = 8000;
const BREATH_REST = 2500;
const BREATH_TOTAL = BREATH_INHALE + BREATH_HOLD + BREATH_EXHALE + BREATH_REST;
const TOTAL_BREATHS = 3;

// ============================================
// TEXT OVERLAY — synced to Michael's audio
// ============================================

const TEXT_SYNC = [
  { t: 1.9, text: 'Now\u2026', dur: 2200 },
  { t: 3.2, text: 'Relax\u2026', dur: 2000 },
  { t: 5.4, text: 'Soften\u2026', dur: 2200 },
  { t: 14.7, text: 'your spine', dur: 3000 },
  { t: 30.0, text: 'very slowly\u2026', dur: 3000 },
  { t: 38.1, text: 'softly\u2026', dur: 2500 },
  { t: 43.1, text: 'no force\u2026', dur: 2500 },
  { t: 50.2, text: 'space\u2026', dur: 3000 },
  { t: 58.0, text: 'calm\u2026', dur: 2500 },
  { t: 64.1, text: 'effortless\u2026', dur: 3000 },
  { t: 73.9, text: 'just allowing\u2026', dur: 3500 },
  { t: 81.4, text: 'feel\u2026', dur: 3000 },
  { t: 84.8, text: 'comfort\u2026', dur: 2500 },
  { t: 90.6, text: 'calm\u2026', dur: 3500 },
  { t: 98.0, text: 'rest\u2026', dur: 5000 },
];

const SUBLIMINAL_FALLBACK = ['safe', 'supported', 'soften', 'ease', 'open'];

// ============================================
// COMPONENT
// ============================================

const HealingImmersion = ({ condition = 'spinal-ease', affirmations = [], journeyLength = null, journeyDurationSeconds = null, onComplete, voiceOverride = null, publicMode = false, keepsakeMeta = null }) => {
  // Resolve timings from explicit seconds (free-play) or a preset ID.
  // Falls back to the legacy 7-min default.
  const { TOTAL_DURATION, DISSOLVE_START, VISUAL_DURATION } = (() => {
    let total = DEFAULT_TOTAL;
    let dissolveOffset = DEFAULT_DISSOLVE_OFFSET;
    if (journeyDurationSeconds && journeyDurationSeconds > 0) {
      total = Math.max(120, Math.min(3600, journeyDurationSeconds));
      // Dissolve for ~10% of the length, capped at 60s / min 20s.
      dissolveOffset = Math.max(20, Math.min(60, Math.round(total * 0.1)));
    } else if (journeyLength && JOURNEY_TIMINGS[journeyLength]) {
      total = JOURNEY_TIMINGS[journeyLength].total;
      dissolveOffset = JOURNEY_TIMINGS[journeyLength].dissolveOffset;
    }
    return {
      TOTAL_DURATION: total,
      DISSOLVE_START: Math.max(VOICE_DURATION + 5, total - dissolveOffset),
      VISUAL_DURATION: total,
    };
  })();
  const [mode, setMode] = useState('waiting'); // waiting → breathing → immersion → dissolving → done
  // On-screen audio-failure marker — visible ONLY when the breath
  // audio element fires an `error` event. This is the anti-silent-
  // failure guard Michael specifically asked for: no more sitting
  // through silence wondering whether anything is happening.
  const [audioIssue, setAudioIssue] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // Narration-linked anatomy: tracks the current highlight target based
  // on how far through the voice recording we are (see /narration-timeline).
  const [narrationTarget, setNarrationTarget] = useState(null);
  const timelineRef = useRef([]);
  // Load the timeline once when the component mounts (spinal-ease only).
  useEffect(() => {
    if (condition !== 'spinal-ease') return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/healing/spinal-ease/narration-timeline`);
        if (r.ok) {
          const d = await r.json();
          timelineRef.current = d.timeline || [];
        }
      } catch { /* no highlights this session — graceful */ }
    })();
  }, [condition]);
  // Poll the voice recording's currentTime and map to a highlight target.
  useEffect(() => {
    if (condition !== 'spinal-ease') return;
    const iv = setInterval(() => {
      const v = voiceRef.current;
      const tl = timelineRef.current;
      if (!v || !tl.length || !v.duration || isNaN(v.duration)) return;
      const pct = v.currentTime / v.duration;
      const entry = tl.find(e => pct >= e.start && pct < e.end);
      setNarrationTarget(entry ? entry.target : null);
    }, 400);
    return () => clearInterval(iv);
  }, [condition]);
  const [muted, setMuted] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [dissolveOpacity, setDissolveOpacity] = useState(0);

  // Breathing state
  const [breathCount, setBreathCount] = useState(0);
  const [breathLabel, setBreathLabel] = useState('');
  const [breathScale, setBreathScale] = useState(1);
  // Canonical phase for BreathOrb: "inhale" | "hold" | "exhale" | "rest"
  const [breathPhase, setBreathPhase] = useState('rest');
  const breathTimerRef = useRef(null);

  // Text overlay
  const [overlayText, setOverlayText] = useState(null);
  const shownTextsRef = useRef(new Set());

  // Subliminal
  const [flashWord, setFlashWord] = useState(null);
  const flashTimerRef = useRef(null);

  // Audio
  const musicRef = useRef(null);
  const voiceRef = useRef(null);
  const binauralRef = useRef(null);
  const breathRef = useRef(null);
  const keepsakeCapturedRef = useRef(false);

  // Timers
  const timerRef = useRef(null);
  const immersionStartRef = useRef(0);

  // ============================================
  // START — gesture handler (Safari safe)
  // Tap unlocks audio context, then we enter breathing
  // ============================================

  const handleStart = useCallback(async () => {
    // iOS Safari REQUIRES audio elements to be created and `.play()`
    // called synchronously inside the user gesture — any `await` before
    // that breaks the gesture context and later `.play()` calls silently
    // fail with NotAllowedError. So we:
    //   1) create the three Audio elements immediately (with fallback URLs)
    //   2) unlock them with a silent `.play().then(pause)` in-gesture
    //   3) THEN await the network fetches and swap `.src` afterwards
    //      (the elements stay unlocked because we already called .play).
    const unlock = (audio) => {
      if (!audio) return;
      // Prime the element inside the gesture — silent play then pause so
      // future programmatic .play() calls succeed on iOS Safari.
      try {
        audio.volume = 0;
        audio.muted = true;
        const p = audio.play();
        if (p && typeof p.then === 'function') {
          p.then(() => { try { audio.pause(); audio.currentTime = 0; audio.muted = false; } catch (_) { /* noop */ } })
           .catch(() => { /* ignore unlock failure — will fall back to visuals */ });
        }
      } catch (_) { /* noop */ }
    };

    // ── Step 1 · create + unlock synchronously ────────────────────
    // If a voiceOverride is supplied (e.g. mum's public link with a
    // pre-selected family voice), use that URL as the INITIAL src so
    // we avoid a 404 → error-state on the fallback file before the
    // swap. Otherwise fall back to the default.
    const initialVoiceUrl = voiceOverride?.url
      || `${API}/api/audio/healing/spinal_light_reset.m4a`;
    try {
      musicRef.current = new Audio(`${API}/api/audio/ambient/summer_breeze.mp3`);
      musicRef.current.loop = true;
      musicRef.current.volume = 0;
      musicRef.current.preload = 'auto';
      try { musicRef.current.load(); } catch (_) { /* noop */ }
      unlock(musicRef.current);

      voiceRef.current = new Audio(initialVoiceUrl);
      voiceRef.current.volume = 0;
      voiceRef.current.preload = 'auto';
      // Explicit .load() forces iOS Safari to start fetching the
      // remote bytes while we're still inside the user gesture —
      // dramatically reducing the chance of a NotSupportedError
      // when we call .play() ~30 s later after the breathing intro.
      try { voiceRef.current.load(); } catch (_) { /* noop */ }
      // Instrument voice failures too — Michael specifically asked
      // that we never sit through silent guidance again.
      voiceRef.current.addEventListener('error', () => {
        const err = voiceRef.current?.error;
        console.error('[HealingImmersion] voice audio failed to load', {
          src: voiceRef.current?.src, code: err?.code, message: err?.message,
        });
        reportAudioError('voice', 0, voiceRef.current, err, { phase: 'initial-load-error' });
      });
      unlock(voiceRef.current);

      binauralRef.current = new Audio(`${API}/api/audio/binaural/theta_6hz_healing_20min.mp3`);
      binauralRef.current.loop = true;
      binauralRef.current.volume = 0;
      binauralRef.current.preload = 'auto';
      try { binauralRef.current.load(); } catch (_) { /* noop */ }
      unlock(binauralRef.current);

      // Constant breath-guidance track — reuse the audio that
      // FamilyCircleTile primed on the user's tap so iOS Safari
      // doesn't drop it when the immersion component mounts.
      //
      // FILE FORMAT NOTE: we prefer `.mp3` here — iOS Safari's most
      // reliable universal codec. Previously we referenced the .m4a
      // (AAC) but AAC/48kHz sample-rate mismatch caused silent
      // playback on some iOS versions. Both files exist server-side;
      // the .mp3 was re-encoded at 44.1kHz + 192kbps specifically for
      // Safari compatibility.
      //
      // CACHE-BUSTER: server file was rebuilt today; any client that
      // cached a broken/empty previous response won't play. Append
      // `?v=` tied to today's asset version so every fresh load
      // fetches from the CDN edge.
      const BREATH_URL = `${API}/api/audio/healing/healing_breath.mp3?v=20260225clean`;
      if (window.__reclaimPrimedBreath) {
        breathRef.current = window.__reclaimPrimedBreath;
        try { breathRef.current.currentTime = 0; } catch (_) { /* noop */ }
        // Feb 25 2026: three breaths ONCE, then Daisy's voice.
        // Looping the breath track kept mum's orb cycling forever
        // and Daisy never arrived. Explicitly disable loop here so
        // the audio-driven sync effect can detect `ended` cleanly.
        breathRef.current.loop = false;
        breathRef.current.volume = 0.55;
        breathRef.current.addEventListener('error', () => {
          reportAudioError('breath', 0, breathRef.current, breathRef.current?.error, { phase: 'primed-error' });
        });
      } else {
        breathRef.current = new Audio();
        // Set preload BEFORE src so iOS Safari starts fetching
        // metadata immediately when the src is assigned.
        breathRef.current.preload = 'auto';
        breathRef.current.src = BREATH_URL;
        breathRef.current.loop = false;
        breathRef.current.volume = 0;
        // Instrument every audio failure mode. Silent .catch(() => {})
        // was the anti-pattern that hid this bug for a week.
        breathRef.current.addEventListener('error', () => {
          const detail = {
            src: breathRef.current?.src,
            code: breathRef.current?.error?.code,
            message: breathRef.current?.error?.message,
          };
          console.error('[HealingImmersion] breath audio failed to load', detail);
          setAudioIssue(`Breath audio couldn't load (code ${detail.code || '?'}). Please hard-refresh the page.`);
        });
        breathRef.current.addEventListener('stalled', () => {
          console.warn('[HealingImmersion] breath audio stalled', { src: breathRef.current?.src });
        });
        breathRef.current.addEventListener('canplay', () => {
          console.log('[HealingImmersion] breath audio ready');
        });
        // Force network fetch to start now (still inside the user
        // gesture on Safari), then unlock via silent play→pause.
        try { breathRef.current.load(); } catch (_) { /* noop */ }
        unlock(breathRef.current);
      }
    } catch (_) { /* audio init failure is non-fatal */ }

    // ── Step 2 · resolve real URLs in background and swap sources ─
    // Music: unified audio resolver.
    try {
      const areaMap = {
        'spinal-ease': 'healing_spinal_ease',
        'heart-flow': 'healing_heart_flow',
        'womb-healing': 'healing_womb',
        'brain-clarity': 'healing_brain_clarity',
        'nervous-system': 'healing_nervous_system',
      };
      const area = areaMap[condition] || 'default';
      const mode = (typeof window !== 'undefined' && window.localStorage.getItem('reclaim_audio_mode')) || 'soft';
      const r = await fetch(`${API}/api/unified-audio/resolve?area=${area}&mode=${mode}`);
      const cfg = await r.json();
      if (cfg?.music?.url && musicRef.current) {
        const musicUrl = cfg.music.url.startsWith('http') ? cfg.music.url : `${API}${cfg.music.url}`;
        if (musicRef.current.src !== musicUrl) {
          musicRef.current.src = musicUrl;
          musicRef.current.load();
        }
      }
    } catch (_) { /* keep fallback */ }

    // Voice: for Spinal Ease we prefer the user's own recording
    // (or the family voice they've chosen). Falls back to Michael's default.
    // In publicMode (e.g. mum's shareable link) we use the caller-provided
    // voiceOverride directly and skip the authenticated fetch.
    if (voiceOverride?.url && voiceRef.current) {
      const voiceUrl = voiceOverride.url;
      if (voiceRef.current.src !== voiceUrl) {
        voiceRef.current.src = voiceUrl;
        voiceRef.current.load();
      }
    } else if (condition === 'spinal-ease' && !publicMode) {
      try {
        const token = localStorage.getItem('reclaim_token') || localStorage.getItem('token') || localStorage.getItem('access_token');
        const r = await fetch(`${API}/api/healing/spinal-ease/voice-config-v2`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r.ok) {
          const cfg = await r.json();
          if (cfg?.voice_url && voiceRef.current) {
            const voiceUrl = cfg.voice_url.startsWith('http') ? cfg.voice_url : `${API}${cfg.voice_url}`;
            if (voiceRef.current.src !== voiceUrl) {
              voiceRef.current.src = voiceUrl;
              voiceRef.current.load();
            }
          }
        }
      } catch (_) { /* keep fallback voice */ }
    }

    // ============================================================
    // CRITICAL · iOS 18.7 Safari silent-block fix (iter279 telemetry)
    // ------------------------------------------------------------
    // The telemetry showed: readyState=4 (audio ready), currentTime=0,
    // paused=true, NO error thrown. iOS 18.7 Safari silently blocks
    // .play() when called outside the user-gesture window — even if
    // the audio was previously primed. React's setState → useEffect
    // is a "next tick" defer that iOS considers outside the gesture.
    //
    // Fix: call breath.play() SYNCHRONOUSLY here (inside the tap
    // handler), before we set mode='breathing'. The sync effect
    // still runs for orb polling, but the actual play/pause is
    // gated by this synchronous call inside the gesture.
    // ============================================================
    if (breathRef.current) {
      try {
        breathRef.current.currentTime = 0;
        breathRef.current.volume = 0.75;
        breathRef.current.loop = false;
        const p = breathRef.current.play();
        if (p && typeof p.then === 'function') {
          p.catch((err) => {
            reportAudioError('breath', 0, breathRef.current, err, { phase: 'handleStart-synchronous-play' });
          });
        }
      } catch (err) {
        reportAudioError('breath', 0, breathRef.current, err, { phase: 'handleStart-throw' });
      }
    }
    // ALSO unlock voice + music + binaural inside the SAME gesture
    // by starting them muted and letting the immersion effect ramp
    // volume up when it's time. iOS Safari treats each Audio
    // element separately — every element that will EVER play
    // needs a play() call inside a user gesture.
    for (const [name, el] of [
      ['voice',    voiceRef.current],
      ['music',    musicRef.current],
      ['binaural', binauralRef.current],
    ]) {
      if (!el) continue;
      try {
        el.muted = true;
        const p = el.play();
        if (p && typeof p.then === 'function') {
          p.then(() => {
            // Immediately pause + rewind — the immersion effect
            // will unmute + replay when it's actually time.
            try { el.pause(); el.currentTime = 0; el.muted = false; } catch (_) { /* noop */ }
          }).catch((err) => {
            reportAudioError(name, 0, el, err, { phase: 'handleStart-unlock' });
          });
        }
      } catch (err) {
        reportAudioError(name, 0, el, err, { phase: 'handleStart-unlock-throw' });
      }
    }

    // Enter breathing — audio will play after 3 breaths
    setMode('breathing');
  }, [condition, voiceOverride, publicMode]);

  // ============================================
  // BREATHING — audio-driven, perfectly synced
  // ------------------------------------------------------------
  // Michael's ask (Feb 25 2026, mum in pain, spine gift):
  //   "It should start with my breathing audio perfectly synced
  //    to the visual breathing orb; after breathing finishes,
  //    the loved one's voice activates in sync with the spine
  //    visual."
  //
  // Previous implementation drove the orb from setTimeout timers
  // that were nominally the same as the audio cadence. iOS
  // Safari's audio buffering + tab throttling could drift the
  // two apart by several seconds. This effect instead POLLS
  // `breathRef.current.currentTime` at 10 Hz and derives the
  // orb's phase / label / scale from the audio's actual playback
  // position — so no matter how long the audio takes to start or
  // whether the tab was briefly backgrounded, the orb is always
  // exactly where Michael's voice is.
  // ============================================

  useEffect(() => {
    if (mode !== 'breathing') return;

    let cancelled = false;
    const audio = breathRef.current;

    // Start the breath audio at t=0 with a healthy volume, then
    // retry twice on iOS Safari NotSupportedError / NotAllowedError
    // (rebuild the element, re-fetch, replay).
    const primeAudio = (attempt = 0) => {
      if (cancelled || !breathRef.current) return;
      const el = breathRef.current;
      try { el.currentTime = 0; } catch (_) { /* iOS can throw here before metadata loads */ }
      el.volume = 0.75;
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.catch((err) => {
          console.warn('[HealingImmersion] breath.play attempt', attempt + 1, 'failed:', err?.name, err?.message);
          reportAudioError('breath', attempt + 1, el, err, { phase: 'sync-effect-play' });
          if (attempt < 2 && !cancelled) {
            const src = el.src;
            try { el.pause(); } catch (_) { /* noop */ }
            const fresh = new Audio(src);
            fresh.loop = true;
            fresh.volume = 0.75;
            fresh.preload = 'auto';
            fresh.addEventListener('error', () => {
              console.error('[HealingImmersion] breath retry failed to load', fresh.error);
              setAudioIssue("Michael's breathing audio couldn't load. Please refresh the page.");
            });
            breathRef.current = fresh;
            setTimeout(() => primeAudio(attempt + 1), 300);
          } else {
            setAudioIssue("Michael's breathing audio couldn't play. Please refresh the page.");
          }
        });
      }
    };
    primeAudio(0);

    // Fade the binaural in quietly under the breath so mum is
    // held by music + theta from the very first inhale.
    if (binauralRef.current) {
      try {
        const BREATHING_BED = 0.18;
        binauralRef.current.play().then(() => {
          let vol = 0;
          const fade = setInterval(() => {
            vol += 0.006;
            if (vol >= BREATHING_BED || !binauralRef.current) {
              clearInterval(fade);
              if (binauralRef.current) binauralRef.current.volume = Math.min(vol, BREATHING_BED);
            } else if (binauralRef.current) {
              binauralRef.current.volume = vol;
            }
          }, 120);
        }).catch(() => {});
      } catch (_) { /* noop */ }
    }

    // ── Drive the orb from a WALL CLOCK (Mar 2026 / Michael) ──
    // Previously the breath tick read audio.currentTime — but when
    // iOS Safari stalls after one breath the currentTime hangs,
    // the orb freezes, and the visitor sees the confused "1 breath
    // then a mess" behaviour mum reported. We now drive both the
    // orb AND the transition to Daisy from a wall clock:
    //   • 3 breaths × 6.5 s = 19.5 s
    //   • Regardless of what the audio element is doing, we always
    //     complete exactly three visible breaths and then hand off
    //     smoothly to the spinal immersion.
    // The audio still plays as a bed underneath if it can.
    const BREATH_LEN_S  = 6.5;   // total length of one visible breath cycle
    const P_INHALE_END  = 0.32;  // 0 → 32% : inhale (2.08 s)
    const P_HOLD_END    = 0.44;  // 32→44%  : brief hold (0.78 s)
    const P_EXHALE_END  = 0.92;  // 44→92%  : long release (3.12 s)
    //                              92→100% : rest (0.52 s)
    const TOTAL_S       = BREATH_LEN_S * TOTAL_BREATHS;  // 19.5 s
    const startedAt = Date.now();

    const tick = setInterval(() => {
      if (cancelled) return;
      const elapsedS = (Date.now() - startedAt) / 1000;
      const breathIndex = Math.min(TOTAL_BREATHS - 1, Math.floor(elapsedS / BREATH_LEN_S));
      const within = Math.min(1, (elapsedS - breathIndex * BREATH_LEN_S) / BREATH_LEN_S);

      let phase, label, scale;
      if (within < P_INHALE_END) {
        phase = 'inhale'; label = 'Breathe in';
        const p = within / P_INHALE_END;
        scale = 1 + (1 - Math.pow(1 - p, 2)) * 0.35;
      } else if (within < P_HOLD_END) {
        phase = 'hold'; label = 'Hold'; scale = 1.35;
      } else if (within < P_EXHALE_END) {
        phase = 'exhale'; label = 'Slowly release';
        const p = (within - P_HOLD_END) / (P_EXHALE_END - P_HOLD_END);
        scale = 1.35 - Math.pow(p, 1.5) * 0.35;
      } else {
        phase = 'rest'; label = ''; scale = 1;
      }
      setBreathPhase(phase);
      setBreathLabel(label);
      setBreathScale(scale);
      setBreathCount(breathIndex);

      // Three breaths complete — smooth handoff to the spinal
      // immersion. Silence and reset the breath audio (belt-and-
      // braces so iOS Safari never bleeds a residual sample).
      if (elapsedS >= TOTAL_S) {
        cancelled = true;
        clearInterval(tick);
        const el = breathRef.current;
        if (el) {
          try {
            el.loop = false;
            el.volume = 0;
            el.pause();
            el.currentTime = 0;
          } catch (_) { /* noop */ }
        }
        // 1400 ms of clean silence before Daisy begins.
        setTimeout(() => { startImmersion(); }, 1400);
      }
    }, 100);

    // Also disable looping on the breath track — we only want the
    // 3 breaths ONCE, not a rolling loop, so the transition into
    // Daisy's voice is clean.
    try { if (audio) audio.loop = false; } catch (_) { /* noop */ }

    return () => {
      cancelled = true;
      clearInterval(tick);
    };
  }, [mode]);

  // ============================================
  // START IMMERSION — called after breathing
  // ============================================

  const startImmersion = useCallback(() => {
    immersionStartRef.current = Date.now();
    setMode('immersion');

    // Belt-and-braces: no matter which code path landed us in
    // immersion, ensure the breath track is truly silent so it
    // can't bleed under Daisy's opening line.
    try {
      if (breathRef.current) {
        breathRef.current.loop = false;
        breathRef.current.volume = 0;
        breathRef.current.pause();
        breathRef.current.currentTime = 0;
      }
    } catch (_) { /* noop */ }

    // Resolve Clear / Soft / Deep volumes for this session.
    const audioMode = getStoredAudioMode();
    const VOICE_TARGET    = voiceVolume(audioMode);
    const MUSIC_TARGET    = musicVolume(audioMode);
    const BINAURAL_TARGET = binauralVolume(audioMode);

    try {
      // Start ambient music — gentle fade in
      if (musicRef.current) {
        musicRef.current.play().then(() => {
          let vol = 0;
          const fade = setInterval(() => {
            vol += 0.002;
            if (vol >= MUSIC_TARGET || !musicRef.current) {
              clearInterval(fade);
              if (musicRef.current) musicRef.current.volume = MUSIC_TARGET;
            } else {
              musicRef.current.volume = vol;
            }
          }, 100);
        }).catch(() => {});
      }

      // Start voice — slight delay for the ambient to establish.
      // Feb 25 2026 · Michael's ask for mum's spine gift: Daisy's
      // voice MUST reliably start after the breathing ends. If the
      // initial play attempt fails, we retry twice with a fresh
      // Audio element so a slow-loading voice doesn't silently
      // disappear (as it did in his first test).
      setTimeout(() => {
        const tryPlayVoice = (attempt = 0) => {
          if (!voiceRef.current) return;
          const el = voiceRef.current;
          // Reset to the start so mum hears the full guidance,
          // not a segment left over from an earlier interaction.
          try { el.currentTime = 0; } catch (_) { /* noop */ }
          const startFade = () => {
            let vol = 0;
            const fade = setInterval(() => {
              vol += 0.04;
              if (vol >= VOICE_TARGET || !voiceRef.current) {
                clearInterval(fade);
                if (voiceRef.current) voiceRef.current.volume = VOICE_TARGET;
              } else {
                voiceRef.current.volume = vol;
              }
            }, 50);
          };
          const p = el.play();
          if (p && typeof p.then === 'function') {
            p.then(startFade).catch((err) => {
              console.warn('[HealingImmersion] voice.play attempt', attempt + 1, 'failed:', err?.name, err?.message);
              reportAudioError('voice', attempt + 1, el, err, { phase: 'immersion-voice-play' });
              if (attempt < 2) {
                // Rebuild the element and try again — often fixes iOS
                // "NotSupportedError" caused by a stale audio source.
                const src = el.src;
                try { el.pause(); } catch (_) { /* noop */ }
                const fresh = new Audio(src);
                fresh.preload = 'auto';
                fresh.volume = 0;
                fresh.muted = false;
                fresh.addEventListener('error', () => {
                  console.error('[HealingImmersion] voice retry failed to load', fresh.error);
                  setAudioIssue("The loved-one's guidance couldn't play. Please refresh the page.");
                });
                try { fresh.load(); } catch (_) { /* noop */ }
                voiceRef.current = fresh;
                setTimeout(() => tryPlayVoice(attempt + 1), 500);
              } else {
                // Final failure — surface visibly. Music + binaural
                // will still carry the session, but Michael wanted
                // to know immediately if guidance drops out.
                console.warn('[HealingImmersion] voice.play failed after retries', err);
                setAudioIssue("The loved-one's guidance couldn't play. Please refresh the page.");
              }
            });
          } else {
            startFade();
          }
        };
        tryPlayVoice(0);
      }, 2200);

      // Binaural bed is already playing quietly (0.18) from the
      // breathing phase — smoothly RAISE it to full "loud music"
      // target as the healing voice arrives. Never reset to 0.
      const isPublicPage = !!publicMode;
      const BOOSTED_BINAURAL = isPublicPage
        ? Math.min(0.55, BINAURAL_TARGET * 2)
        : BINAURAL_TARGET;
      if (binauralRef.current) {
        if (binauralRef.current.paused) {
          binauralRef.current.play().catch(() => {});
        }
        let vol = binauralRef.current.volume || 0.18;
        const rise = setInterval(() => {
          vol += 0.005;
          if (vol >= BOOSTED_BINAURAL || !binauralRef.current) {
            clearInterval(rise);
            if (binauralRef.current) binauralRef.current.volume = BOOSTED_BINAURAL;
          } else if (binauralRef.current) {
            binauralRef.current.volume = vol;
          }
        }, 100);
      }

      // Breath guide is already playing (started at breathing mode
      // begin). We hold it at target here — do NOT reset to 0 or we'd
      // cut off mid-cycle.
    } catch (e) { /* audio start failure is non-fatal */ }
  }, []);

  // ============================================
  // IMMERSION TIMER + TEXT SYNC + DISSOLVE
  // ============================================

  useEffect(() => {
    if (mode !== 'immersion' && mode !== 'dissolving') return;

    timerRef.current = setInterval(() => {
      const secs = (Date.now() - immersionStartRef.current) / 1000;
      setElapsed(secs);

      // Synced text overlay (during voice)
      if (secs < VOICE_DURATION + 10) {
        for (const item of TEXT_SYNC) {
          if (Math.abs(secs - item.t) < 0.6 && !shownTextsRef.current.has(item.t)) {
            shownTextsRef.current.add(item.t);
            setOverlayText(item.text);
            setTimeout(() => setOverlayText(null), item.dur);
            break;
          }
        }
      }

      // After voice ends — bring ambient up gently
      if (secs > VOICE_DURATION + 3 && musicRef.current && musicRef.current.volume < 0.15) {
        musicRef.current.volume = Math.min(0.15, musicRef.current.volume + 0.001);
      }

      // Begin slow dissolve
      if (secs >= DISSOLVE_START && mode !== 'dissolving') {
        setMode('dissolving');

        // Capture keepsake at the peak — right as dissolve kicks in.
        // Best-effort / fire-and-forget so the visual is never blocked.
        if (keepsakeMeta && !keepsakeCapturedRef.current) {
          keepsakeCapturedRef.current = true;
          captureAndSendKeepsake(keepsakeMeta);
        }

        // Very slowly fade out all audio
        const fadeAudio = setInterval(() => {
          let allDone = true;
          if (musicRef.current && musicRef.current.volume > 0.002) {
            musicRef.current.volume = Math.max(0, musicRef.current.volume - 0.001);
            allDone = false;
          }
          if (binauralRef.current && binauralRef.current.volume > 0.002) {
            binauralRef.current.volume = Math.max(0, binauralRef.current.volume - 0.002);
            allDone = false;
          }
          if (breathRef.current && breathRef.current.volume > 0.002) {
            breathRef.current.volume = Math.max(0, breathRef.current.volume - 0.002);
            allDone = false;
          }
          if (allDone) clearInterval(fadeAudio);
        }, 120);
      }

      // Dissolve opacity builds up very gradually
      if (secs >= DISSOLVE_START) {
        const dissolveProg = Math.min(1, (secs - DISSOLVE_START) / (TOTAL_DURATION - DISSOLVE_START));
        setDissolveOpacity(dissolveProg);
      }

      // Complete
      if (secs >= TOTAL_DURATION) {
        clearInterval(timerRef.current);
        if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
        if (voiceRef.current) { voiceRef.current.pause(); voiceRef.current = null; }
        if (binauralRef.current) { binauralRef.current.pause(); binauralRef.current = null; }
        if (breathRef.current) { breathRef.current.pause(); breathRef.current = null; }
        onComplete();
      }
    }, 250);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [mode, onComplete]);

  // ============================================
  // SUBLIMINAL — full affirmations, at the edge of perception
  // Visible enough to sense activity but not readable
  // ============================================

  const subliminalPool = affirmations.length > 0 ? affirmations : SUBLIMINAL_FALLBACK;

  const triggerFlash = useCallback(() => {
    const text = subliminalPool[Math.floor(Math.random() * subliminalPool.length)];
    // Subtle: opacity 0.03–0.06 (a fraction below "too bold")
    const opacity = 0.03 + Math.random() * 0.03;
    const positions = [
      { left: `${6 + Math.random() * 20}%`, top: `${20 + Math.random() * 55}%` },
      { left: `${72 + Math.random() * 20}%`, top: `${20 + Math.random() * 55}%` },
      { left: `${20 + Math.random() * 60}%`, top: `${6 + Math.random() * 14}%` },
      { left: `${20 + Math.random() * 60}%`, top: `${78 + Math.random() * 14}%` },
    ];
    setFlashWord({ text, opacity, position: positions[Math.floor(Math.random() * positions.length)] });
    // Faster cycle — shorter display, quicker rotation
    setTimeout(() => setFlashWord(null), 600 + Math.random() * 800);
    flashTimerRef.current = setTimeout(triggerFlash, 2000 + Math.random() * 2500);
  }, [subliminalPool]);

  useEffect(() => {
    if (mode !== 'immersion') return;
    // Start subliminals 20s into immersion (earlier than before)
    flashTimerRef.current = setTimeout(triggerFlash, 20000);
    return () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); };
  }, [mode, triggerFlash]);

  // Show skip after 5s (from any active mode)
  useEffect(() => {
    if (mode === 'waiting') return;
    const t = setTimeout(() => setShowSkip(true), 5000);
    return () => clearTimeout(t);
  }, [mode]);

  // Controls
  const handleSkip = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    if (breathTimerRef.current) {
      clearTimeout(breathTimerRef.current.holdTimer);
      clearTimeout(breathTimerRef.current.exhaleTimer);
      clearTimeout(breathTimerRef.current.restTimer);
    }
    if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
    if (voiceRef.current) { voiceRef.current.pause(); voiceRef.current = null; }
    if (binauralRef.current) { binauralRef.current.pause(); binauralRef.current = null; }
    if (breathRef.current) { breathRef.current.pause(); breathRef.current = null; }
    onComplete();
  };

  const toggleMute = () => {
    setMuted(m => {
      const v = !m;
      if (musicRef.current) musicRef.current.muted = v;
      if (voiceRef.current) voiceRef.current.muted = v;
      if (binauralRef.current) binauralRef.current.muted = v;
      if (breathRef.current) breathRef.current.muted = v;
      return v;
    });
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (breathTimerRef.current) {
        clearTimeout(breathTimerRef.current.holdTimer);
        clearTimeout(breathTimerRef.current.exhaleTimer);
        clearTimeout(breathTimerRef.current.restTimer);
      }
      if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
      if (voiceRef.current) { voiceRef.current.pause(); voiceRef.current = null; }
      if (breathRef.current) { breathRef.current.pause(); breathRef.current = null; }
    };
  }, []);

  const formatMins = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const progress = Math.min(100, (elapsed / TOTAL_DURATION) * 100);

  return (
    <motion.div
      className="fixed inset-0 overflow-hidden select-none"
      style={{ background: '#060906' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
      data-testid="healing-immersion"
    >
      {/* Audio-issue pill — surfaces silent-failure the moment it
          happens. Hidden during immersion so it never covers the
          spinal visual (Michael, Mar 2026 — mum needs the full
          screen for the visual). Errors during immersion still fire
          reportAudioError for the console + telemetry. */}
      {audioIssue && mode !== 'immersion' && mode !== 'dissolving' && (
        <div
          className="fixed top-4 left-1/2 z-[200] pointer-events-none"
          style={{ transform: 'translateX(-50%)' }}
          data-testid="healing-audio-issue"
        >
          <div
            className="px-3 py-2 rounded-full text-[11px] tracking-[0.15em] uppercase max-w-[300px] text-center"
            style={{
              background: 'rgba(30, 12, 6, 0.85)',
              color: '#F4C67C',
              border: '1px solid rgba(244, 198, 124, 0.4)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {audioIssue}
          </div>
        </div>
      )}
      {/* HEALING VISUAL ENGINE — Spinal Ease uses the anatomical
          lumbar engine. Mar 2026 (Michael): mount ONLY during the
          spinal immersion so the breath phase has a pure pulsating
          orb — no spine bleed-through, no confusion. The spine
          fades in cleanly at the immersion handoff. */}
      {(mode === 'immersion' || mode === 'dissolving') && (
        condition === 'spinal-ease' ? (
          <CrystalSpineVisualEngine
            elapsed={elapsed}
            totalDuration={VISUAL_DURATION}
            isActive={mode === 'immersion' || mode === 'dissolving'}
            narrationTarget={narrationTarget}
          />
        ) : (
          <HealingVisualEngine
            condition="spine"
            elapsed={elapsed}
            totalDuration={VISUAL_DURATION}
            isActive={mode === 'immersion' || mode === 'dissolving'}
          />
        )
      )}

      {/* SUBLIMINAL FLASH — affirmations at the edge of perception */}
      <AnimatePresence>
        {flashWord && mode !== 'dissolving' && (
          <motion.div
            className="absolute pointer-events-none"
            style={{
              left: flashWord.position.left,
              top: flashWord.position.top,
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
              maxWidth: '40%',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: flashWord.opacity }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm text-white/80 tracking-wide"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: 'italic',
                textShadow: '0 0 40px rgba(200,170,100,0.06)',
                mixBlendMode: 'soft-light',
                filter: 'blur(0.3px)',
              }}>
              {flashWord.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SYNCED TEXT OVERLAY */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 25 }}>
        <AnimatePresence mode="wait">
          {overlayText && mode === 'immersion' && (
            <motion.p
              key={overlayText}
              className="text-center text-2xl md:text-3xl text-white/35 tracking-[0.1em]"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
            >
              {overlayText}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* TAP TO BEGIN
          Feb 25 2026 · Michael's mum reported the flow "froze" on
          her first attempt. Root cause was almost certainly this
          overlay reading as static text on a small iPhone screen
          right after she tapped Daisy — she didn't realise a
          SECOND tap was needed. Replaced the tiny italic "Tap to
          enter" line with an oversized pulsing golden button that
          is impossible to miss, and kept the whole overlay as a
          tap zone so any tap works. */}
      {mode === 'waiting' && (
        <motion.div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          style={{ zIndex: 50, background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(10px)' }}
          onClick={handleStart}
          data-testid="healing-tap-begin"
        >
          <div className="flex flex-col items-center gap-8 text-center">
            <p className="text-white/45 text-[10px] tracking-[0.32em] uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {condition === 'spinal-ease' ? 'Spinal Healing & Recovery' : 'Spinal Light Reset'}
            </p>
            <motion.button
              type="button"
              onClick={handleStart}
              whileTap={{ scale: 0.96 }}
              className="relative flex items-center justify-center rounded-full"
              style={{
                width: 220, height: 220,
                background: 'radial-gradient(circle at 50% 45%, #f6d68a 0%, #d59f4f 45%, #8a5b26 100%)',
                boxShadow: '0 0 70px rgba(245,200,130,0.55), 0 0 140px rgba(200,130,80,0.35), inset 0 6px 26px rgba(255,240,200,0.5), inset 0 -6px 26px rgba(120,60,30,0.5)',
              }}
              data-testid="healing-tap-begin-button"
            >
              <motion.span
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{ border: '1px solid rgba(255,224,170,0.55)' }}
                animate={{ scale: [1, 1.12, 1], opacity: [0.6, 0.1, 0.6] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
              />
              <span
                className="text-[#3a1e08] text-2xl tracking-wide"
                style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 600, letterSpacing: '0.05em' }}
              >
                Tap to begin
              </span>
            </motion.button>
            <p className="text-white/40 italic text-sm max-w-xs" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Take the phone in one hand, tap the button, then rest it on your chest.
            </p>
            <p className="text-white/25 text-[11px]" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {Math.round(TOTAL_DURATION / 60)} minutes
            </p>
          </div>
        </motion.div>
      )}

      {/* BREATHING PHASE — 3 silent breaths before immersion */}
      <AnimatePresence>
        {mode === 'breathing' && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            style={{ zIndex: 50, background: 'rgba(6,9,6,0.85)', backdropFilter: 'blur(12px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            data-testid="healing-breathing-phase"
          >
            <div className="flex flex-col items-center justify-center">
              <p className="text-white/25 text-[10px] tracking-[0.3em] uppercase mb-14"
                style={{ fontFamily: "'Manrope', sans-serif" }}>
                Settle in
              </p>

              {/* Canonical BreathOrb with impact halo — the orb sits
                  inside an expanding ripple that pulses on inhale so
                  the breath feels alive. Michael asked for a more
                  impactful arrival visual: the ripple + larger orb +
                  concentric outer rings give the moment presence
                  without overwhelming Nanna's eyes. */}
              <div className="relative mb-12 flex items-center justify-center" style={{ width: 260, height: 260 }}>
                {/* Concentric ripples — 3 rings, each phase-driven */}
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={`ripple-${i}`}
                    className="absolute rounded-full pointer-events-none"
                    style={{
                      width: 260,
                      height: 260,
                      border: `1px solid rgba(230, 200, 140, ${0.22 - i * 0.06})`,
                    }}
                    animate={{
                      scale: breathPhase === 'inhale' ? [1, 1.18 + i * 0.06]
                            : breathPhase === 'hold'  ? 1.18 + i * 0.06
                            : breathPhase === 'exhale'? [1.18 + i * 0.06, 1]
                            : 1,
                      opacity: breathPhase === 'inhale' ? [0.35 - i * 0.07, 0]
                             : breathPhase === 'exhale'? [0, 0.35 - i * 0.07]
                             : 0.15,
                    }}
                    transition={{
                      duration: breathPhase === 'inhale' ? 5
                              : breathPhase === 'exhale' ? 8
                              : 2,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
                <BreathOrb
                  size={200}
                  phase={breathPhase}
                  tone="gold"
                  data-testid="healing-breath-orb"
                />
              </div>

              {/* Breath label */}
              <AnimatePresence mode="wait">
                {breathLabel && (
                  <motion.p
                    key={breathLabel}
                    className="text-white/70 text-lg tracking-wide"
                    style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    {breathLabel}
                  </motion.p>
                )}
              </AnimatePresence>

              {/* Counter */}
              <p className="text-white/25 text-[10px] tracking-[0.28em] uppercase mt-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {Math.min(breathCount + 1, TOTAL_BREATHS)} of {TOTAL_BREATHS}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SLOW DISSOLVE OVERLAY — very gradually fades to black */}
      {dissolveOpacity > 0 && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            zIndex: 35,
            background: '#060906',
            opacity: dissolveOpacity * 0.85,
          }}
        />
      )}

      {/* DISSOLVE TEXT — appears as the screen fades */}
      <AnimatePresence>
        {mode === 'dissolving' && dissolveOpacity > 0.3 && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 40 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: Math.min(1, dissolveOpacity * 1.5) }}
          >
            <motion.p
              className="text-white/45 text-lg"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 4 }}
            >
              Let the body integrate
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CONTROLS */}
      {(mode === 'immersion' || mode === 'dissolving') && dissolveOpacity < 0.5 && (
        <div className="absolute top-6 right-6 z-40">
          <motion.button onClick={toggleMute} className="p-2.5 rounded-full"
            style={{ background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(8px)' }}
            whileTap={{ scale: 0.9 }} data-testid="healing-mute-btn">
            {muted ? <VolumeX size={14} className="text-white/40" /> : <Volume2 size={14} className="text-white/40" />}
          </motion.button>
        </div>
      )}

      {/* SKIP — z-60 to be above breathing phase overlay (z-50) */}
      <AnimatePresence>
        {showSkip && mode !== 'waiting' && dissolveOpacity < 0.3 && (
          <motion.button
            className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full text-xs text-white/30"
            style={{ zIndex: 60, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(8px)', fontFamily: "'Manrope', sans-serif" }}
            onClick={handleSkip} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            whileTap={{ scale: 0.95 }} data-testid="healing-skip-btn">
            <SkipForward size={11} /> Skip
          </motion.button>
        )}
      </AnimatePresence>

      {/* PROGRESS */}
      {(mode === 'immersion' || mode === 'dissolving') && dissolveOpacity < 0.5 && (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-40 text-center">
          <div className="w-48 h-px bg-white/10 rounded-full overflow-hidden mb-3">
            <div className="h-full rounded-full" style={{ background: 'rgba(200,170,100,0.35)', width: `${progress}%`, transition: 'width 1s linear' }} />
          </div>
          <p className="text-white/20 text-[10px]" style={{ fontFamily: "'Manrope', sans-serif" }}>{formatMins(elapsed)}</p>
        </div>
      )}

      <div className="absolute bottom-3 left-0 right-0 text-center z-30">
        <p className="text-white/6 text-[9px]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          This experience supports relaxation alongside your medical care.
        </p>
      </div>
    </motion.div>
  );
};

export default HealingImmersion;
